import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject } from "@angular/core";
import { SubscriptionTracker } from "@shared-helpers/subscriptionTracker";
import { BaseServiceOperationResult } from "@shared-results/BaseServiceOperationResult";
import { ServiceOperationOutcome } from "@shared-results/ServiceOperationOutcome";
import { ErrorService } from "@shared-services/error.service";
import { connectable, Observable, Observer, of } from "rxjs";

export class BaseHttpService {

  get httpOptions(): { headers?: HttpHeaders } {
    return BaseHttpService.getHttpOptions();
  }
  readonly friendlyName: string;
  
  protected httpTimeout: number = 60000;

  protected http = inject(HttpClient);
  protected errorService = inject(ErrorService);
  protected subscriptionTracker = inject(SubscriptionTracker);

  constructor(friendlyName: string) { this.friendlyName = friendlyName; }

  protected handleOperationResult<T extends BaseServiceOperationResult>(url: string, body: string, httpOptions: { headers?: HttpHeaders }, result?: T,
    options?: {
      /**
       * If defined then this method must return true for the result to be counted as a success
       * @param r
       * @returns
       */
      additionalSuccessCriteria?: (r: T) => boolean,
      /**
       * If true and the result is not a success then log the details
       */
      logIfNotSuccess?: boolean,
      /**
       * If true and the result is not a success then throw an error
       */
      throwIfNotSuccess?: boolean,
      /**
       * If defined and this method returns true then any log entry will be a warning rather than an error
       * @param r
       * @returns
       */
      logAsWarningCriteria?: (r: T) => boolean
    }
  ): Observable<T> {
    // Always check for success first
    if (!(result && result.ServiceOperationOutcome === ServiceOperationOutcome.Success && (typeof options?.additionalSuccessCriteria !== 'function' || options?.additionalSuccessCriteria(result)))) {
      // Not success, work out what to do
      let logError = typeof options?.logIfNotSuccess !== 'undefined' ? options?.logIfNotSuccess : true;
      let throwError = typeof options?.throwIfNotSuccess !== 'undefined' ? options?.throwIfNotSuccess : false;

      if (logError || throwError) {
        // Need to construct error message
        let headers: string = BaseHttpService.tryGetHttpRequestHeaders(httpOptions);
        let message: string = `Error calling method '${url}' from '${this.friendlyName}', ServiceOperationOutcome is not success, body: '${body}', headers: '${headers}', result: ${JSON.stringify(result)}`;
        let errorObject: Error = new Error(message);

        if (logError) {
          // Need to log error, check if we need to log as warning
          let logAsWarning = false;
          if (!result) {
            // Result is empty, log as warning
            logAsWarning = true;
          }
          if (!logAsWarning && typeof options?.logAsWarningCriteria === 'function') {
            logAsWarning = options?.logAsWarningCriteria(result);
          }
          // Send log request
          this.errorService?.logError(message, url, errorObject, logAsWarning);
        }

        if (throwError) {
          // Need to throw error
          throw errorObject;
        }
      }
    }
    return of(result);
  }

  throwErrorIfNotSuccess<T extends BaseServiceOperationResult>(url: string, body: string, httpOptions: { headers?: HttpHeaders }, result?: T, additionalSuccessCriteria?: (r: T) => boolean): Observable<T> {
    if (!(result && result.ServiceOperationOutcome === ServiceOperationOutcome.Success && (!additionalSuccessCriteria || additionalSuccessCriteria(result)))) {
      let headers: string = BaseHttpService.tryGetHttpRequestHeaders(httpOptions);
      throw new Error(`Error calling method '${url}' from '${this.friendlyName}', ServiceOperationOutcome is not success, body: '${body}', headers: '${headers}', result: ${JSON.stringify(result)}`);
    }
    return of(result);
  }

  logErrorIfNotSuccess<T extends BaseServiceOperationResult>(url: string, body: string, httpOptions: { headers?: HttpHeaders }, result?: T, logAsWarningCriteria: (r: T) => boolean = null): Observable<T> {
    if (!(result && result.ServiceOperationOutcome === ServiceOperationOutcome.Success)) {
      let logAsWarning = false;
      if (!result) {
        // Result is empty, log as warning
        logAsWarning = true;
      }
      if (!logAsWarning && logAsWarningCriteria) {
        logAsWarning = logAsWarningCriteria(result);
      }
      let headers: string = BaseHttpService.tryGetHttpRequestHeaders(httpOptions);
      let message: string = `Error calling method '${url}' from '${this.friendlyName}', ServiceOperationOutcome is not success, body: '${body}', headers: '${headers}', result: ${JSON.stringify(result)}`;
      this.errorService?.logError(message, url, new Error(message), logAsWarning);
    }
    return of(result);
  }

  protected addSubscriptions<T>(source: Observable<T>, ...observerOrNextList: Array<Partial<Observer<T>> | ((value: T) => void)>): void {
    // Ensure subscription added to source to trigger it
    if (!observerOrNextList || observerOrNextList?.length < 1) {
      // No observers provided, use empty subscribe
      source?.subscribe();
    }
    else if (observerOrNextList?.length == 1) {
      // Exactly one observer provided, subscribe
      source?.subscribe(observerOrNextList[0]);
    }
    else {
      // Multiple observers provided
      // Create a multicast source based on original source
      var connectableSource = connectable(source);
      // Add each observer in the list
      observerOrNextList.forEach((observerOrNext) => {
        connectableSource.subscribe(observerOrNext);
      });
      // Connect to original source, this will trigger it
      connectableSource.connect();
    }
  }

  protected getPartialObserver<T>(observerOrNext?: Partial<Observer<T>> | ((value: T) => void)): Partial<Observer<T>> {
    // Ensure we have methods defined so that we can use
    let partialObserver: Partial<Observer<T>>;
    if (typeof observerOrNext === 'function') {
      partialObserver = {
        next: (observerOrNext ?? undefined) as (((value: T) => void) | undefined),
        error: (err: any) => { },
        complete: () => { },
      };
    }
    else {
      partialObserver = observerOrNext;
      partialObserver = {
        next: partialObserver.next ?? ((value: T) => { }),
        error: partialObserver.error ?? ((err: any) => { }),
        complete: partialObserver.complete ?? (() => { }),
      };
    }
    return partialObserver;
  }

  static getHttpOptionsWithResponseObserver(httpOptions: { headers?: HttpHeaders }): { headers?: HttpHeaders, observe: 'response', responseType: 'json' } {
    return { headers: httpOptions?.headers, observe: 'response', responseType: 'json' };
  }

  static getHttpOptions(customHeaders: {} = {}, includeWebsiteVersion: boolean = true): { headers?: HttpHeaders } {
    if (!customHeaders) customHeaders = {};

    if (includeWebsiteVersion) {
      let versionString = null;
      try {
        let versionElement = document.getElementById('fourd_version');
        if (versionElement) {
          versionString = versionElement.textContent;
        }
      }
      catch (e) { }
      customHeaders = Object.assign(customHeaders, { 'X-Website-Version': versionString ?? '' });
    }

    return { headers: BaseHttpService.getCustomHttpHeaders(customHeaders) };
  }

  static getCustomHttpHeaders(customHeaders: {}): HttpHeaders {
    // Get default headers for all requests
    let headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      'Pragma': 'no-cache',
      'Expires': '0'
    });
    try {
      for (const [key, value] of Object.entries(customHeaders)) {
        if (key?.length > 0) {
          headers = headers.append(key, value?.toString() ?? ''); // Append headers, this is a clone operation
        }
      }
    }
    catch (e) { }
    return headers;
  }

  static tryGetHttpRequestHeaders(httpOptions: { headers?: HttpHeaders }): string {
    let headers: string = "";
    try {
      let headerObject: Record<string, string[]> = {};
      httpOptions?.headers?.keys()?.forEach(
        (key) => {
          headerObject[key as keyof {}] = httpOptions?.headers?.getAll(key);
        }
      );
      headers = JSON.stringify(headerObject);
    }
    catch (e) { }
    return headers;
  }

}
